import qs from "qs";

export type QueryParameterSerialization = {
  style?: "form" | "spaceDelimited" | "pipeDelimited" | "deepObject";
  explode?: boolean;
};

export const serializeQueryParameters = (
  queryParams: Record<string, unknown>,
  serialization: Record<string, QueryParameterSerialization>,
) =>
  Object.entries(queryParams)
    .map(([name, value]) =>
      serializeQueryParameter(name, value, serialization[name]),
    )
    .filter(Boolean)
    .join("&");

const serializeQueryParameter = (
  name: string,
  value: unknown,
  serialization?: QueryParameterSerialization,
) => {
  if (!serialization) {
    return qs.stringify({ [name]: value });
  }

  switch (serialization.style) {
    case "deepObject":
      return qs.stringify(
        { [name]: value },
        { arrayFormat: "indices", encodeValuesOnly: true },
      );
    case "spaceDelimited":
      return serializeDelimitedArray(name, value, "%20");
    case "pipeDelimited":
      return serializeDelimitedArray(name, value, "|");
    case "form":
    default:
      return serializeFormParameter(name, value, serialization.explode);
  }
};

const serializeFormParameter = (
  name: string,
  value: unknown,
  explode = true,
) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const entries = Object.entries(value);

    if (explode) {
      return qs.stringify(Object.fromEntries(entries), {
        arrayFormat: "repeat",
        encodeValuesOnly: true,
      });
    }

    return `${encodeURIComponent(name)}=${entries
      .flatMap(([key, entryValue]) => [key, String(entryValue)])
      .map(encodeURIComponent)
      .join(",")}`;
  }

  return qs.stringify(
    { [name]: value },
    {
      arrayFormat: explode ? "repeat" : "comma",
      encodeValuesOnly: true,
    },
  );
};

const serializeDelimitedArray = (
  name: string,
  value: unknown,
  delimiter: string,
) => {
  if (!Array.isArray(value)) {
    return qs.stringify({ [name]: value }, { encodeValuesOnly: true });
  }

  return `${encodeURIComponent(name)}=${value
    .map((item) => encodeURIComponent(String(item)))
    .join(delimiter)}`;
};